import { adapt, fail, ok } from '@/api/apiResultAdapter'
import { ERROR_CODES } from '@/2D3DEditer/ports/errorCodes'
import { generateIdempotencyKey } from '@/utils/generateIdempotencyKey'

const DEFAULT_PROJECT_TYPE = 'FLOORPLAN_DESIGN'

/**
 * ProjectCatalogPort adapter for ai-design-project catalog APIs.
 *
 * @param {{
 *   createAiDesignProject?: (payload: object) => Promise<unknown>,
 *   fetchAiDesignProjectList?: (params: object) => Promise<unknown>,
 *   fetchAiDesignProject?: (projectKey: string) => Promise<unknown>,
 *   deleteAiDesignProject?: (projectKey: string) => Promise<unknown>,
 *   saveAiDesignProjectEditorJson?: (projectKey: string, data: unknown) => Promise<unknown>
 * }} [deps]
 */
export function createMhBackendProjectAdapter(deps = {}) {
  return {
    /**
     * @param {{ pageNum?: number, pageSize?: number, projectType?: string, content?: string }} [params]
     */
    async listProjects(params = {}) {
      const fetchList = await resolveHttp(deps, 'fetchAiDesignProjectList')
      const result = await adapt(fetchList({
        pageNum: Math.max(1, Number(params.pageNum) || 1),
        pageSize: Math.max(1, Number(params.pageSize) || 6),
        projectType: params.projectType || DEFAULT_PROJECT_TYPE,
        content: params.content || ''
      }))
      if (!result.ok) return result

      const page = readEnvelopeData(result.data)
      if (!page || typeof page !== 'object' || Array.isArray(page)) {
        return fail({
          code: ERROR_CODES.VALIDATION_ERROR,
          message: 'Project list response is missing data'
        })
      }

      const items = Array.isArray(page.data) ? page.data : []
      return ok({
        count: Number(page.count) || 0,
        pages: Number(page.pages) || 0,
        data: items.map(mapProjectRecord)
      })
    },

    /**
     * @param {{ projectName: string, idempotencyKey?: string, projectType?: string }} input
     */
    async createProject(input = {}) {
      const createProject = await resolveHttp(deps, 'createAiDesignProject')
      const projectName = String(input.projectName || '').trim()
      const idempotencyKey = String(input.idempotencyKey || '').trim() || generateIdempotencyKey()
      const result = await adapt(createProject({
        projectName,
        projectType: String(input.projectType || '').trim() || DEFAULT_PROJECT_TYPE,
        idempotencyKey
      }))
      if (!result.ok) return result
      return mapRecordResult(result.data, 'Create project response is missing data')
    },

    /**
     * @param {string} projectKey
     */
    async getProject(projectKey) {
      const fetchProject = await resolveHttp(deps, 'fetchAiDesignProject')
      const result = await adapt(fetchProject(projectKey))
      if (!result.ok) return result
      return mapRecordResult(result.data, 'Project detail response is missing data')
    },

    /**
     * @param {string} projectKey
     * @param {object} editorJson
     */
    async saveEditorJson(projectKey, editorJson) {
      const saveEditor = await resolveHttp(deps, 'saveAiDesignProjectEditorJson')
      const form = createMultipartForm()
      form.append('editorJson', toJsonPart(editorJson), 'editor.json')
      const result = await adapt(saveEditor(projectKey, form))
      if (!result.ok) return result
      return mapRecordResult(result.data, 'Save editor JSON response is missing data')
    },

    /**
     * @param {string[]} projectKeys
     */
    async deleteProjects(projectKeys = []) {
      const deleteProject = await resolveHttp(deps, 'deleteAiDesignProject')
      const keys = Array.isArray(projectKeys)
        ? Array.from(new Set(projectKeys.map(value => String(value || '').trim()).filter(Boolean)))
        : []
      if (!keys.length) {
        return fail({
          code: ERROR_CODES.VALIDATION_ERROR,
          message: 'projectIds is required'
        })
      }

      for (const projectKey of keys) {
        const result = await adapt(deleteProject(projectKey))
        if (!result.ok) return result
      }
      return ok({ deleted: keys.length })
    }
  }
}

/**
 * Choose the OSS object key for loading a remote scene.
 * `extraInfo.editorJsonPath` wins over `result_json_key` parsed from the latest
 * successful design's `resultData`. Returns a locator key, never a public URL.
 *
 * @param {object} detail
 * @returns {string|null}
 */
export function parseProjectExtraInfo(extraInfo) {
  if (!extraInfo) return null
  if (typeof extraInfo === 'object' && !Array.isArray(extraInfo)) return extraInfo
  if (typeof extraInfo !== 'string') return null
  try {
    const parsed = JSON.parse(extraInfo)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch (_error) {
    return null
  }
}

export function resolveRemoteSceneLocator(detail) {
  const extraInfo = parseProjectExtraInfo(detail && detail.extraInfo)
  if (extraInfo) {
    const editorJsonPath = extraInfo.editorJsonPath
    if (editorJsonPath) return String(editorJsonPath)
  }

  const designs = detail && Array.isArray(detail.successDesigns) ? detail.successDesigns : []
  const latest = designs[0]
  if (!latest || latest.resultData == null || latest.resultData === '') return null

  const parsed = parseResultData(latest.resultData)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const resultJsonKey = parsed.result_json_key
  if (!resultJsonKey) return null
  return String(resultJsonKey)
}

function parseResultData(resultData) {
  if (resultData && typeof resultData === 'object') return resultData
  if (typeof resultData !== 'string') return null
  try {
    return JSON.parse(resultData)
  } catch (error) {
    return null
  }
}

function mapRecordResult(adaptedData, missingMessage) {
  const record = readEnvelopeData(adaptedData)
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return fail({
      code: ERROR_CODES.VALIDATION_ERROR,
      message: missingMessage
    })
  }
  return ok(mapProjectRecord(record))
}

function mapProjectRecord(raw) {
  const idempotencyKey = raw && raw.idempotencyKey != null ? String(raw.idempotencyKey) : ''
  return {
    ...raw,
    projectId: idempotencyKey,
    extraInfo: raw.extraInfo,
    successDesigns: raw.successDesigns == null ? [] : raw.successDesigns
  }
}

function readEnvelopeData(adaptedData) {
  if (!adaptedData || typeof adaptedData !== 'object' || Array.isArray(adaptedData)) {
    return null
  }
  const payload = adaptedData.data
  if (payload == null) return null
  if (typeof payload !== 'object') return null
  return payload
}

async function resolveHttp(deps, exportName) {
  if (typeof deps[exportName] === 'function') return deps[exportName]
  const api = await import('@/api')
  return api[exportName]
}

function toJsonPart(value) {
  const text = typeof value === 'string' ? value : JSON.stringify(value)
  if (typeof Blob === 'function') {
    return new Blob([text], { type: 'application/json' })
  }
  return text
}

function createMultipartForm() {
  if (typeof FormData === 'function') return new FormData()
  return new SimpleFormData()
}

function SimpleFormData() {
  this._entries = []
}

SimpleFormData.prototype.append = function append(key, value, filename) {
  this._entries.push({ key, value, filename })
}

SimpleFormData.prototype.get = function get(key) {
  const hit = this._entries.find(entry => entry.key === key)
  return hit ? hit.value : null
}

SimpleFormData.prototype.has = function has(key) {
  return this._entries.some(entry => entry.key === key)
}

SimpleFormData.prototype.entries = function entries() {
  return this._entries.slice()
}
